import { Button, ScrollView, SizableText, Spinner, Stack, TextArea, XStack, YStack } from '@my/ui'
import { UserAvatar } from 'app/features/user/components'
import { Calendar, ChevronDown, ImagePlus, Megaphone, X } from '@tamagui/lucide-icons'
import { Group, User } from 'app/types'
import { SolitoImage } from 'solito/image'
import { PostCreationHeader } from '../post-creation-header'
import { useState } from 'react'
import { useImagePicker } from 'app/hooks'
import { ChangePostTypeButton } from '../change-post-type-button'
import { PageType } from '../group-post-creation-wizard'

export interface PostFormData {
  text?: string
  image: string | null
}

export interface SubmitPostData extends PostFormData {
  group: Pick<Group, 'id' | 'name'>
}

type PostCreationViewProps = {
  title: string
  user: Pick<User, 'id' | 'fullName' | 'avatar'>
  defaultValues?: Omit<SubmitPostData, 'group'>
  selectedGroup?: Pick<Group, 'id' | 'name'> | null
  onOpenChange: (open: boolean) => void
  onGroupSelectPress?: () => void
  onPageChange: (page: PageType) => void
  onSubmit: (data: SubmitPostData, cb: () => void) => void
  isSubmitting?: boolean
  isEditing?: boolean
  isGroupOwner?: boolean
}

export const PostCreationView = ({
  title,
  user,
  selectedGroup,
  defaultValues,
  onOpenChange,
  onGroupSelectPress,
  onPageChange,
  onSubmit,
  isSubmitting,
  isEditing,
  isGroupOwner,
}: PostCreationViewProps) => {
  const [text, setText] = useState<string>(defaultValues?.text || '')
  const [postImage, setPostImage] = useState<string | null>(defaultValues?.image || null)
  const { pickImage, loading: pickImageLoading } = useImagePicker({
    onChange: (image) => setPostImage(image.uri),
    constraints: {
      minWidth: 100,
      minHeight: 100,
    },
    resize: {
      width: 1000,
    },
  })

  // Allow posting if there is text or an image and a group is selected
  const canPost = (text.length > 1 || !!postImage) && !!selectedGroup?.id

  const handleSubmit = async () => {
    if (!selectedGroup || !canPost) {
      return
    }

    onSubmit(
      {
        group: selectedGroup,
        text,
        image: postImage,
      },
      () => {
        setText('')
        setPostImage(null)
      }
    )
  }

  return (
    <YStack flex={1} gap="$3.5">
      {/* Header */}
      <PostCreationHeader title={title} onClose={() => onOpenChange(false)}>
        <Button
          size="$3"
          onPress={handleSubmit}
          theme="primary"
          disabled={!canPost}
          opacity={canPost ? 1 : 0.5}
          loading={isSubmitting}
          $gtSm={{
            size: '$4',
          }}
        >
          Publish
        </Button>
      </PostCreationHeader>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flex: 1 }}>
        <YStack flex={1} gap="$3.5">
          <XStack w="100%" gap="$3.5" ai="flex-start">
            <UserAvatar src={user?.avatar?.small} width={44} height={44} />
            <YStack flex={1} gap="$1">
              <SizableText size="$3" fow="500">
                {user?.fullName}
              </SizableText>
              <XStack gap="$2" flexWrap="wrap">
                {onGroupSelectPress && (
                  <Button
                    theme="gray"
                    size="$1"
                    px="$1.5"
                    onPress={onGroupSelectPress}
                    iconAfter={<ChevronDown size={12} />}
                  >
                    <SizableText size="$2" fow="500" numberOfLines={1} maxWidth={100}>
                      {selectedGroup ? selectedGroup.name : 'Select Group'}
                    </SizableText>
                  </Button>
                )}
                {!selectedGroup && !isEditing && (
                  <SizableText size="$1">Select a group to publish</SizableText>
                )}
              </XStack>
            </YStack>
          </XStack>

          {/* Text Area */}
          <YStack p="$3.5" gap="$3.5" flex={1} boc="$gray5" bw={1} br="$3" maxHeight={400}>
            <TextArea
              flex={1}
              bw={0}
              p="$0"
              focusStyle={{ outlineWidth: 0 }}
              multiline
              placeholder={`What's on your mind, ${user.fullName}?`}
              fow="500"
              color="$gray9"
              onChangeText={setText}
              value={text || ''}
              textAlignVertical="top"
              maxLength={1000}
            />

            {!postImage && (
              <Button
                mt="auto"
                flexGrow={0}
                mr="auto"
                theme="gray"
                size="$2"
                w={40}
                h={40}
                loading={pickImageLoading}
                icon={<ImagePlus size={24} />}
                onPress={pickImage}
              />
            )}

            {text.length > 500 && (
              <SizableText
                pos="absolute"
                bottom="$3.5"
                right="$3.5"
                color="$overlay10"
                size="$2"
                fow="500"
              >
                {text.length}/1000
              </SizableText>
            )}

            {postImage && (
              <Stack w={100} h={100} pos="relative">
                <Stack w="100%" h="100%" overflow="hidden" br="$3">
                  {pickImageLoading && <Spinner />}
                  <SolitoImage src={postImage} fill contentFit="cover" alt="" />
                </Stack>
                <Button
                  size="$2"
                  circular
                  aria-label="Remove image"
                  pos="absolute"
                  top="$-2"
                  right="$-2"
                  theme="gray"
                  onPress={() => setPostImage(null)}
                  icon={<X size={16} />}
                />
              </Stack>
            )}
          </YStack>
          {!isEditing && (
            <YStack gap="$2">
              <ChangePostTypeButton
                onPress={() => onPageChange('createEvent')}
                label="Create an event"
                icon={<Calendar size={24} />}
              />
              {isGroupOwner && (
                <ChangePostTypeButton
                  onPress={() => onPageChange('createAnnouncement')}
                  label="Create an announcement"
                  icon={<Megaphone size={24} />}
                />
              )}
            </YStack>
          )}
        </YStack>
      </ScrollView>
    </YStack>
  )
}
